import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Download, ExternalLink, RefreshCw, Tag as TagIcon } from "lucide-react";
import { downloadFile } from "@/lib/api";
import type { ABVersion, RecipientFilter, ReportDetail, ReportHour, ReportSummary } from "@/lib/types";
import { formatCents, formatDateTime, formatInt, formatPercent, formatTime, formatWhen } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { ColumnChart } from "@/components/ColumnChart";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { TagPicker } from "../contacts/ContactHeader";
import { KIND_LABEL, RECIPIENT_FILTERS, useRebuild, useRecipients, useReport, useTagRecipients } from "./reportHooks";
import { LabelChip } from "./EmailLabel";

export function ReportDetailPage() {
  const { reportId } = useParams({ from: "/email/reports/$reportId" });
  const id = Number(reportId);
  const q = useReport(id);
  if (q.isLoading) return <Skeleton className="h-96" />;
  if (!q.data) return <p className="text-sm text-[var(--color-destructive)]">{q.error?.message ?? "Report not found."}</p>;
  return <Report id={id} rep={q.data} />;
}

function Report({ id, rep }: { id: number; rep: ReportDetail }) {
  const s = rep.summary;
  const rebuild = useRebuild(id);
  const toast = useToast();
  const imported = s.kind.startsWith("keap_");
  return (
    <div className="mx-auto max-w-[1300px] space-y-3">
      <Link to="/email/reports" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Email reports
      </Link>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold">{s.name}</h1>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[var(--color-muted-foreground)]">
            {s.brand_id && <BrandMark id={s.brand_id} />}
            <LabelChip label={rep.label} />
            <span>{KIND_LABEL[s.kind] ?? s.kind}</span>
            {s.first_sent_at && <span>sent {formatWhen(s.first_sent_at)}{s.last_sent_at && s.last_sent_at !== s.first_sent_at ? ` to ${formatTime(s.last_sent_at)}` : ""}</span>}
            {s.segment_name && <span>to {s.segment_name}</span>}
          </div>
        </div>
        <Button size="sm" variant="outline" asChild>
          <Link to="/email/reports" search={{ segment: s.segment_id ?? undefined }}>
            Other sends to this segment
          </Link>
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={rebuild.isPending}
          title="Recount every number from the per-message data (after an import, or if something looks off)"
          onClick={() => rebuild.mutate(undefined, { onSuccess: () => toast("Numbers recounted"), onError: (e) => toast(e.message, "error") })}
        >
          <RefreshCw className={cn(rebuild.isPending && "animate-spin")} /> Recount
        </Button>
      </div>

      <Headline s={s} imported={imported} />

      {rep.ab_test && <ABCard ab={rep.ab_test} />}

      <div className="grid gap-3 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Hour by hour after the send</CardTitle>
            <span className="text-xs text-[var(--color-muted-foreground)]">Mountain Time</span>
          </CardHeader>
          <CardContent>
            <HourlyCharts s={s} hours={rep.hourly} imported={imported} />
          </CardContent>
        </Card>
        <LinksCard id={id} rep={rep} />
      </div>

      <Recipients id={id} s={s} />
    </div>
  );
}

function Stat({ label, value, sub, title }: { label: string; value: string; sub?: string; title?: string }) {
  return (
    <div className="min-w-0 px-4 py-3" title={title}>
      <div className="text-xs text-[var(--color-muted-foreground)]">{label}</div>
      <div className="text-xl font-semibold leading-tight">{value}</div>
      {sub && <div className="num text-xs leading-snug text-[var(--color-muted-foreground)]">{sub}</div>}
    </div>
  );
}

function Headline({ s, imported }: { s: ReportSummary; imported: boolean }) {
  return (
    <Card className="grid grid-cols-2 divide-x divide-y sm:grid-cols-4 sm:divide-y-0 xl:grid-cols-8">
      <Stat label="Sent" value={formatInt(s.sent)} sub={imported ? "imported from Keap" : undefined} />
      <Stat label="Delivered" value={imported ? "—" : formatPercent(s.delivered / Math.max(s.sent, 1))} sub={imported ? "Keap didn't record deliveries" : `${formatInt(s.delivered)} people`} />
      <Stat
        label="Opened by a person"
        value={formatPercent(s.human_open_rate)}
        sub={`${formatInt(s.human_opens_unique)} people`}
        title="Machine opens (Apple Mail Privacy Protection, image proxies) are left out of this number"
      />
      <Stat label="Opened, incl. machines" value={formatPercent(s.open_rate)} sub={`${formatInt(s.opens_unique)} (${formatInt(s.opens_total)} opens)`} />
      <Stat label="Clicked" value={formatPercent(s.click_rate)} sub={`${formatInt(s.clicks_unique)} people (${formatPercent(s.click_to_open_rate)} of openers)`} />
      <Stat label="Unsubscribed" value={formatInt(s.unsubscribes)} sub={formatPercent(s.unsubscribe_rate)} />
      <Stat label="Bounced" value={formatInt(s.bounces_hard + s.bounces_soft)} sub={`${s.bounces_hard} hard, ${s.bounces_soft} soft; ${s.complaints} spam report${s.complaints === 1 ? "" : "s"}`} />
      <Stat
        label="Revenue"
        value={formatCents(s.revenue_cents)}
        sub={`${formatInt(s.orders)} order${s.orders === 1 ? "" : "s"} within ${s.attribution_window_days} days of a click`}
        title={`Orders paid within ${s.attribution_window_days} days after the person's last click on this email`}
      />
    </Card>
  );
}

type HourRow = { h: number; at: string; human: number; machine: number; clicks: number } & Record<string, number | string>;

function HourlyCharts({ s, hours, imported }: { s: ReportSummary; hours: ReportHour[]; imported: boolean }) {
  const [span, setSpan] = useState<48 | 168>(48);
  const data = useMemo(() => {
    const byH = new Map(hours.map((h) => [h.hours_after_send, h]));
    const start = s.first_sent_at ? new Date(s.first_sent_at) : new Date();
    start.setMinutes(0, 0, 0);
    const out: HourRow[] = [];
    for (let i = 0; i < span; i++) {
      const h = byH.get(i);
      out.push({
        h: i,
        at: new Date(start.getTime() + i * 3600_000).toISOString(),
        human: Math.max(0, (h?.opens ?? 0) - (h?.machine_opens ?? 0)),
        machine: h?.machine_opens ?? 0,
        clicks: h?.clicks ?? 0,
      });
    }
    return out;
  }, [hours, s.first_sent_at, span]);
  const later = hours.filter((h) => h.hours_after_send >= span).reduce((a, h) => a + h.opens, 0);
  if (imported && hours.length === 0) return <Empty title="Keap's history has open and click dates per person, not per hour, so there's no hourly chart for imported emails." />;
  const label = (r: HourRow) => (r.h % 24 === 0 ? (r.h === 0 ? "send" : `day ${r.h / 24 + 1}`) : `${r.h}h`);
  const title = (r: HourRow) => `Hour ${r.h + 1} after the send (${formatWhen(r.at)})`;
  return (
    <div className="space-y-4">
      <div role="radiogroup" aria-label="How long after the send" className="flex gap-1">
        {([48, 168] as const).map((n) => (
          <Button key={n} size="sm" role="radio" aria-checked={span === n} variant={span === n ? "default" : "outline"} onClick={() => setSpan(n)}>
            {n === 48 ? "First 2 days" : "First week"}
          </Button>
        ))}
      </div>
      <ColumnChart
        title="Opens per hour"
        data={data}
        xKey="h"
        xLabel={label}
        tooltipTitle={title}
        series={[
          { key: "human", label: "By people", color: "var(--color-chart-opens)" },
          { key: "machine", label: "By machines (Apple Mail etc.)", color: "var(--color-chart-machine)" },
        ]}
      />
      <ColumnChart title="Clicks per hour" data={data} xKey="h" xLabel={label} tooltipTitle={title} height={140} series={[{ key: "clicks", label: "Clicks", color: "var(--color-chart-clicks)" }]} />
      {later > 0 && <p className="text-xs text-[var(--color-muted-foreground)]">{formatInt(later)} more opens came after this window.</p>}
    </div>
  );
}

/** A broadcast's A/B subject test: each subject line's people and results side by side, and the server's plain verdict. */
function ABCard({ ab }: { ab: { versions: ABVersion[]; verdict: string } }) {
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>A/B subject test</CardTitle>
        <span className="text-xs text-[var(--color-muted-foreground)]">people, not machine opens or security-scanner clicks; refunded orders left out</span>
      </CardHeader>
      <Table>
        <THead>
          <tr>
            <TH>Subject line</TH>
            <TH className="text-right">Sent</TH>
            <TH className="text-right">Delivered</TH>
            <TH className="text-right">Opened</TH>
            <TH className="text-right">Clicked</TH>
            <TH className="text-right">Unsubscribed</TH>
            <TH className="text-right">Spam</TH>
            <TH className="text-right">Orders</TH>
            <TH className="text-right">Revenue</TH>
          </tr>
        </THead>
        <tbody>
          {ab.versions.map((v) => (
            <TR key={v.version}>
              <TD className="max-w-[24rem]">
                <span className="mr-2 font-semibold">{v.version.toUpperCase()}</span>
                <span className="break-words">{v.subject}</span>
              </TD>
              <TD className="num text-right">{formatInt(v.sent)}</TD>
              <TD className="num text-right">{formatInt(v.delivered)}</TD>
              <TD className="num text-right" title={`${formatInt(v.people_opened)} people`}>
                {formatPercent(v.open_rate)}
              </TD>
              <TD className="num text-right" title={`${formatInt(v.people_clicked)} people`}>
                {formatPercent(v.click_rate)} <span className="text-xs text-[var(--color-muted-foreground)]">({formatInt(v.people_clicked)})</span>
              </TD>
              <TD className="num text-right">{formatInt(v.unsubscribes)}</TD>
              <TD className="num text-right">{formatInt(v.complaints)}</TD>
              <TD className="num text-right">{formatInt(v.orders)}</TD>
              <TD className="num text-right">{v.revenue_cents ? formatCents(v.revenue_cents) : ""}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
      <CardContent className="pt-3 text-sm">{ab.verdict}</CardContent>
    </Card>
  );
}

function LinksCard({ id, rep }: { id: number; rep: ReportDetail }) {
  const navigate = useNavigate({ from: "/email/reports/$reportId" });
  const max = Math.max(1, ...rep.links.map((l) => l.clicks_unique));
  return (
    <Card className="overflow-x-auto">
      <CardHeader>
        <CardTitle>Clicks per link</CardTitle>
        <span className="text-xs text-[var(--color-muted-foreground)]">security-scanner clicks left out</span>
      </CardHeader>
      {rep.links.length === 0 ? (
        <Empty title="Nobody clicked a link yet." />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Link</TH>
              <TH className="text-right">People</TH>
              <TH className="text-right">Clicks</TH>
            </tr>
          </THead>
          <tbody>
            {rep.links.map((l) => (
              <TR key={l.link_key}>
                <TD className="max-w-[22rem]">
                  <div className="flex items-center gap-1.5">
                    <button
                      className="min-w-0 truncate text-left text-[var(--color-primary)] hover:underline"
                      title={`Show who clicked ${l.url}`}
                      onClick={() => navigate({ search: { list: "clicked", link: l.link_key }, hash: "people" })}
                    >
                      {prettyUrl(l.url)}
                    </button>
                    <a href={l.url} target="_blank" rel="noreferrer noopener" aria-label={`Open ${l.url}`} className="shrink-0 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                      <ExternalLink className="size-3" />
                    </a>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-[var(--color-muted)]" aria-hidden>
                    <div className="h-full rounded-full bg-[var(--color-chart-clicks)]" style={{ width: `${(l.clicks_unique / max) * 100}%` }} />
                  </div>
                </TD>
                <TD className="num text-right align-top">{formatInt(l.clicks_unique)}</TD>
                <TD className="num text-right align-top">{formatInt(l.clicks_total)}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      <p className="border-t px-4 py-2 text-xs text-[var(--color-muted-foreground)]">Click a link to list the people who clicked it. Report #{id}.</p>
    </Card>
  );
}

function prettyUrl(u: string): string {
  try {
    const x = new URL(u);
    return x.hostname.replace(/^www\./, "") + x.pathname.replace(/\/$/, "") + (x.search ? "?…" : "");
  } catch {
    return u || "(unknown link)";
  }
}

function Recipients({ id, s }: { id: number; s: ReportSummary }) {
  const search = useSearch({ from: "/email/reports/$reportId" });
  const navigate = useNavigate({ from: "/email/reports/$reportId" });
  const filter = (RECIPIENT_FILTERS.find((f) => f.value === search.list)?.value ?? "human_opened") as RecipientFilter;
  const link = filter === "clicked" ? search.link : undefined;
  const q = useRecipients(id, filter, link);
  const rows = q.data?.pages.flatMap((p) => p.recipients) ?? [];
  const toast = useToast();
  const tag = useTagRecipients(id);
  const [picking, setPicking] = useState(false);
  const [confirm, setConfirm] = useState<{ id: number; name: string } | null>(null);
  const label = RECIPIENT_FILTERS.find((f) => f.value === filter)?.label ?? filter;
  const count = RECIPIENT_FILTERS.find((f) => f.value === filter)?.count?.(s);
  const csv = async () => {
    try {
      const p = new URLSearchParams({ filter });
      if (link) p.set("link_key", link);
      const name = await downloadFile(`/api/v1/email-reports/${id}/recipients.csv?${p}`, undefined, `email-report-${id}-${filter}.csv`);
      toast(`Downloaded ${name}`);
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  return (
    <Card id="people" className="overflow-x-auto">
      <CardHeader className="flex-wrap">
        <CardTitle>Who</CardTitle>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={csv}>
            <Download /> Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPicking(true)}>
            <TagIcon /> Tag these people
          </Button>
        </div>
      </CardHeader>
      <div role="radiogroup" aria-label="Which people" className="flex flex-wrap gap-1 border-b px-4 py-2">
        {RECIPIENT_FILTERS.map((f) => {
          const n = f.count?.(s);
          return (
            <button
              key={f.value}
              role="radio"
              aria-checked={filter === f.value}
              onClick={() => navigate({ search: { list: f.value }, replace: true })}
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-xs",
                filter === f.value ? "border-[var(--color-primary)] bg-[var(--color-accent)] text-[var(--color-accent-foreground)]" : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]",
              )}
            >
              {f.label}
              {n !== undefined && <span className="num ml-1 font-normal">{formatInt(n)}</span>}
            </button>
          );
        })}
      </div>
      {link && (
        <div className="flex items-center gap-2 border-b px-4 py-1.5 text-xs">
          <Badge variant="default">only people who clicked {prettyUrl(link.replace(/^u:/, ""))}</Badge>
          <button className="text-[var(--color-primary)] hover:underline" onClick={() => navigate({ search: { list: "clicked" }, replace: true })}>
            Any link
          </button>
        </div>
      )}
      {q.isLoading && <Skeleton className="m-3 h-40" />}
      {!q.isLoading && rows.length === 0 && <Empty title="Nobody in this list." />}
      {rows.length > 0 && (
        <Table>
          <THead>
            <tr>
              <TH>Person</TH>
              <TH>Email</TH>
              <TH>Delivered</TH>
              <TH>First opened</TH>
              <TH className="text-right">Opens</TH>
              <TH>First click</TH>
              <TH className="text-right">Clicks</TH>
              <TH>Problem</TH>
              <TH className="text-right">Revenue</TH>
            </tr>
          </THead>
          <tbody>
            {rows.map((r) => (
              <TR key={r.contact_id}>
                <TD className="max-w-[14rem]">
                  <Link to="/contacts/$contactId" params={{ contactId: String(r.contact_id) }} className="block truncate text-[var(--color-primary)] hover:underline">
                    {r.name || `#${r.contact_id}`}
                  </Link>
                </TD>
                <TD className="max-w-[16rem] truncate">{r.email}</TD>
                <TD className="num whitespace-nowrap">{r.delivered_at ? formatTime(r.delivered_at) : "—"}</TD>
                <TD className="num whitespace-nowrap">
                  {r.opened_at ? formatDateTime(r.opened_at) : "—"}
                  {r.opened_at && !r.human_opened_at && <Badge variant="muted" className="ml-1">machine</Badge>}
                </TD>
                <TD className="num text-right">{r.open_count || ""}</TD>
                <TD className="num whitespace-nowrap">{r.clicked_at ? formatDateTime(r.clicked_at) : "—"}</TD>
                <TD className="num text-right">{r.click_count || ""}</TD>
                <TD>
                  {r.bounced_at && <Badge variant="destructive">{r.bounce_type ?? ""} bounce</Badge>}
                  {r.unsubscribed_at && <Badge variant="muted">unsubscribed</Badge>}
                  {r.complained_at && <Badge variant="destructive">spam report</Badge>}
                </TD>
                <TD className="num text-right">{r.revenue_cents ? formatCents(r.revenue_cents) : ""}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="border-t p-2">
          <Button size="sm" variant="ghost" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            Show 100 more
          </Button>
        </div>
      )}
      <TagPicker title="Tag everyone in this list" open={picking} onOpenChange={setPicking} onPick={(tid, name) => setConfirm({ id: tid, name })} />
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogTitle>
            Add “{confirm?.name}” to everyone in “{label}”{count !== undefined ? ` (${formatInt(count)})` : ""}?
          </DialogTitle>
          <DialogDescription>
            Each person's tag history will show it came from this email report. Adding a tag can start automations. Up to 25,000 people per run.
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              disabled={tag.isPending}
              onClick={() =>
                confirm &&
                tag.mutate(
                  { filter, link_key: link, tag_id: confirm.id },
                  {
                    onSuccess: (r) => {
                      toast(`Tagged ${formatInt(r.tagged)} ${r.tagged === 1 ? "person" : "people"} with ${confirm.name}${r.matched > r.tagged ? ` (${formatInt(r.matched - r.tagged)} already had it)` : ""}${r.truncated ? ". Stopped at 25,000." : ""}`);
                      setConfirm(null);
                    },
                    onError: (e) => toast(e.message, "error"),
                  },
                )
              }
            >
              {tag.isPending ? "Tagging…" : "Add tag"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
